/**
 * Export ops checklist definitions (with steps and instructions) to Excel.
 */

import type { Checklist, Weekday } from "@/services/api/opsChecklists";
import type { Workbook, Worksheet } from "exceljs";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const WEEKDAY_LABELS: Record<Weekday, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

/** Format schedule badges the same way as the config list UI. */
export function formatChecklistSchedule(checklist: Checklist): string {
  const parts: string[] = [];
  if (checklist.schedule_days_of_week.length > 0) {
    parts.push(
      checklist.schedule_days_of_week
        .map((day) => WEEKDAY_LABELS[day] ?? day)
        .join(", ")
    );
  }
  if (checklist.schedule_days_of_month.length > 0) {
    parts.push(`Day ${checklist.schedule_days_of_month.join(", ")}`);
  }
  if (parts.length === 0) {
    return "Daily";
  }
  return parts.join(" · ");
}

/** Reduce markdown instructions to plain text for spreadsheet cells. */
export function instructionsForExcel(markdown: string): string {
  let text = markdown.trim();
  if (!text) return "";

  text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt: string, url: string) =>
    alt ? `${alt} (${url})` : url
  );
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");
  text = text.replace(/\*\*([^*]+)\*\*/g, "$1");
  text = text.replace(/__([^_]+)__/g, "$1");
  text = text.replace(/\*([^*\n]+)\*/g, "$1");
  text = text.replace(/_([^_\n]+)_/g, "$1");
  text = text.replace(/^#{1,6}\s+/gm, "");
  text = text.replace(/`([^`]+)`/g, "$1");
  text = text.replace(/^[•·▪▸]\s*/gm, "- ");
  return text;
}

function styleHeaderRow(sheet: Worksheet) {
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle", wrapText: true };
  sheet.views = [{ state: "frozen", ySplit: 1 }];
}

function sortedChecklists(checklists: Checklist[]): Checklist[] {
  return [...checklists].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
}

function sortedSteps(checklist: Checklist) {
  return [...checklist.steps].sort((a, b) => a.order - b.order);
}

export async function buildChecklistsWorkbook(
  checklists: Checklist[]
): Promise<Workbook> {
  const exceljs = await import("exceljs");
  const workbook = new exceljs.Workbook();
  workbook.creator = "Ani Ops Checklists";
  workbook.created = new Date();

  const summarySheet = workbook.addWorksheet("Checklists");
  summarySheet.columns = [
    { header: "Checklist", key: "name", width: 32 },
    { header: "Status", key: "status", width: 12 },
    { header: "Description", key: "description", width: 40 },
    { header: "Key points", key: "bullet_points", width: 36 },
    { header: "Schedule", key: "schedule", width: 22 },
    { header: "Default assignee", key: "default_assignee", width: 18 },
    { header: "Deadline", key: "deadline", width: 12 },
    { header: "Timmy review", key: "timmy_review_default", width: 14 },
    { header: "TTL (days)", key: "ttl_days", width: 12 },
    { header: "Steps", key: "step_count", width: 10 },
  ];

  const stepsSheet = workbook.addWorksheet("Steps");
  stepsSheet.columns = [
    { header: "Checklist", key: "checklist_name", width: 32 },
    { header: "Checklist status", key: "checklist_status", width: 14 },
    { header: "Step #", key: "step_order", width: 10 },
    { header: "Step title", key: "step_title", width: 36 },
    { header: "Instructions", key: "instructions", width: 64 },
  ];

  for (const checklist of sortedChecklists(checklists)) {
    summarySheet.addRow({
      name: checklist.name,
      status: checklist.is_active ? "Active" : "Disabled",
      description: checklist.description ?? "",
      bullet_points: (checklist.bullet_points ?? []).join("\n"),
      schedule: formatChecklistSchedule(checklist),
      default_assignee: checklist.default_assignee ?? "",
      deadline: checklist.deadline ?? "",
      timmy_review_default: checklist.timmy_review_default ?? "required",
      ttl_days: checklist.ttl_days,
      step_count: checklist.steps.length,
    });

    for (const step of sortedSteps(checklist)) {
      stepsSheet.addRow({
        checklist_name: checklist.name,
        checklist_status: checklist.is_active ? "Active" : "Disabled",
        step_order: step.order,
        step_title: step.title,
        instructions: instructionsForExcel(step.instructions),
      });
    }
  }

  styleHeaderRow(summarySheet);
  styleHeaderRow(stepsSheet);
  stepsSheet.getColumn("instructions").alignment = { wrapText: true, vertical: "top" };

  return workbook;
}

export async function buildChecklistsXlsx(checklists: Checklist[]): Promise<Blob> {
  const workbook = await buildChecklistsWorkbook(checklists);
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}

export function checklistsExportFilename(date = new Date()): string {
  const stamp = date.toISOString().slice(0, 10);
  return `ops_checklists_${stamp}.xlsx`;
}

export async function downloadChecklistsXlsx(checklists: Checklist[]): Promise<void> {
  const blob = await buildChecklistsXlsx(checklists);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = checklistsExportFilename();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
